import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

interface Props {
  value: string;
  onChange: (text: string) => void;
}

export const VoiceCapture = ({ value, onChange }: Props) => {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef<any>(null);
  const baseRef = useRef("");

  useEffect(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";

    recognition.onresult = (event: any) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += chunk + " ";
        else interimText += chunk;
      }
      if (finalText) {
        baseRef.current = `${baseRef.current}${finalText}`.replace(/\s+/g, " ");
        onChange(baseRef.current.trim());
      }
      setInterim(interimText);
    };

    recognition.onerror = (e: any) => {
      if (e.error === "not-allowed") toast.error("Microphone permission denied");
      else if (e.error !== "aborted" && e.error !== "no-speech") toast.error(`Speech error: ${e.error}`);
      setRecording(false);
    };

    recognition.onend = () => {
      setInterim("");
      setRecording(false);
    };

    recognitionRef.current = recognition;
    return () => {
      try { recognition.stop(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = () => {
    if (!recognitionRef.current) return;
    baseRef.current = value ? `${value} ` : "";
    try {
      recognitionRef.current.start();
      setRecording(true);
    } catch {
      toast.error("Could not start recording");
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  if (!supported) {
    return (
      <Alert>
        <AlertDescription>
          Your browser does not support speech recognition. Use the <strong>Text</strong> tab to type your
          update instead (Chrome and Edge support voice input).
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-muted-foreground/30 p-8">
        <button
          type="button"
          onClick={recording ? stop : start}
          aria-label={recording ? "Stop recording" : "Start recording"}
          className={`relative flex h-20 w-20 items-center justify-center rounded-full transition-colors ${
            recording ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground hover:opacity-90"
          }`}
        >
          {recording && <span className="absolute inset-0 animate-ping rounded-full bg-destructive/60" />}
          {recording ? <Square className="relative h-7 w-7" /> : <Mic className="relative h-8 w-8" />}
        </button>
        <p className="text-sm font-medium">
          {recording ? "Recording… speak now" : "Tap the microphone and describe your business activity"}
        </p>
        {recording && (
          <span className="flex items-center gap-2 text-xs text-destructive">
            <span className="h-2 w-2 animate-pulse rounded-full bg-destructive" /> Listening
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Transcript (editable)</label>
          {value && (
            <Button variant="ghost" size="sm" onClick={() => { baseRef.current = ""; onChange(""); }}>
              <Trash2 className="mr-1 h-3 w-3" /> Clear
            </Button>
          )}
        </div>
        <Textarea
          value={interim ? `${value} ${interim}`.trim() : value}
          onChange={(e) => { baseRef.current = e.target.value; onChange(e.target.value); }}
          rows={6}
          placeholder="Your spoken words will appear here as you talk…"
        />
      </div>
    </div>
  );
};
